import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  Clock, Phone, Target, TrendingUp, Calendar, Users,
  Coffee, Utensils, LogOut, Play, Pause, CheckCircle,
  XCircle, BarChart3, Download, Filter, Award,
  Activity, PieChart, ChevronDown, ChevronRight,
  Zap, Timer, Rocket, Star, Crown, Sparkles,
  Headphones, Briefcase, Wifi, WifiOff, RefreshCw,
  Circle, CircleDot, CircleOff, Settings, Moon, Sun,
  Power, Coffee as CoffeeIcon, Home, TrendingDown,
  DollarSign, Trophy, LineChart, Eye, Download as DownloadIcon
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  LineChart as ReLineChart, Line, BarChart as ReBarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart as RePieChart, Pie, Cell
} from 'recharts';
import * as XLSX from 'xlsx';

export default function Dashboard() {
  const { user, profile } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('daily');
  
  // ========== AGENT STATE ==========
  const [stats, setStats] = useState(null);
  const [presence, setPresence] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [pauseType, setPauseType] = useState(null);
  const [pauseStart, setPauseStart] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [workingSeconds, setWorkingSeconds] = useState(0);
  const [pause1Seconds, setPause1Seconds] = useState(0);
  const [pause2Seconds, setPause2Seconds] = useState(0);
  const [lunchSeconds, setLunchSeconds] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [animatedStats, setAnimatedStats] = useState({
    calls: 0,
    leads: 0,
    qualified: 0,
    converted: 0,
    commission: 0
  });

  // ========== MANAGER STATE ==========
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamPresence, setTeamPresence] = useState({});
  const [teamStats, setTeamStats] = useState({});
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [leaderboard, setLeaderboard] = useState([]);
  const [commissions, setCommissions] = useState({});
  const [callTracking, setCallTracking] = useState({});

  const isMounted = useRef(true);
  const today = new Date().toISOString().split('T')[0];

  // ========== RÔLE ==========
  const isAgent = profile?.role === 'agent';
  const isManager = ['manager', 'admin', 'super_admin'].includes(profile?.role);

  // ========== FORMATAGE ==========
  const formatDuration = (s) => {
    if (!s) return '0h 00min 00s';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}h ${m.toString().padStart(2, '0')}min ${sec.toString().padStart(2, '0')}s`;
  };

  const formatMinutes = (m) => {
    if (!m) return '0min';
    const h = Math.floor(m / 60);
    const min = m % 60;
    return h > 0 ? `${h}h ${min}min` : `${min}min`;
  };

  const formatTime = (d) => d?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) || '-';
  const formatCurrency = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val || 0);

  // ========== GET START DATE ==========
  const getStartDate = () => {
    const date = new Date();
    switch(selectedPeriod) {
      case 'daily': return date.toISOString().split('T')[0];
      case 'weekly': 
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        return weekStart.toISOString();
      case 'monthly':
        const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
        return monthStart.toISOString();
      default: return date.toISOString();
    }
  };

  // ========== TIMERS AGENT ==========
  useEffect(() => {
    if (!isAgent) return;
    const timer = setInterval(() => {
      if (isMounted.current) setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, [isAgent]);

  useEffect(() => {
    if (!isAgent) return;
    let workInterval, pause1Interval, pause2Interval, lunchInterval;
    
    if (presence?.start_time && !presence?.end_time) {
      const startTime = new Date(presence.start_time).getTime();
      
      workInterval = setInterval(() => {
        const now = Date.now();
        const totalElapsed = Math.floor((now - startTime) / 1000);
        const totalPauseSeconds = pause1Seconds + pause2Seconds + lunchSeconds;
        setWorkingSeconds(totalElapsed - totalPauseSeconds);
      }, 1000);
    }

    if (isPaused) {
      const pauseStartTime = pauseStart?.getTime();
      if (!pauseStartTime) return;
      
      if (pauseType === 'pause1') {
        pause1Interval = setInterval(() => {
          setPause1Seconds(Math.floor((Date.now() - pauseStartTime) / 1000));
        }, 1000);
      } else if (pauseType === 'pause2') {
        pause2Interval = setInterval(() => {
          setPause2Seconds(Math.floor((Date.now() - pauseStartTime) / 1000));
        }, 1000);
      } else if (pauseType === 'lunch') {
        lunchInterval = setInterval(() => {
          setLunchSeconds(Math.floor((Date.now() - pauseStartTime) / 1000));
        }, 1000);
      }
    }

    return () => {
      clearInterval(workInterval);
      clearInterval(pause1Interval);
      clearInterval(pause2Interval);
      clearInterval(lunchInterval);
    };
  }, [isAgent, presence, isPaused, pauseType, pauseStart]);

  // ========== LOAD AGENT PRESENCE ==========
  const loadAgentPresence = async () => {
    if (!isAgent || !user) return;
    try {
      const { data, error } = await supabase
        .from('agent_presences')
        .select('*')
        .eq('agent_id', user.id)
        .eq('date', today)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && data && isMounted.current) {
        setPresence(data);
        setPause1Seconds(0); 
        setPause2Seconds(0); 
        setLunchSeconds(0);
        
        if (data.pause_start && !data.pause_end) {
          setIsPaused(true);
          setPauseType(data.pause_type);
          setPauseStart(new Date(data.pause_start));
        }
      }
    } catch (error) {
      console.error('Erreur chargement présence:', error);
    }
  };

  // ========== LOAD AGENT STATS ==========
  const loadAgentStats = async () => {
    if (!isAgent || !user) return;
    try {
      const startDate = getStartDate();
      const [callsRes, leadsRes] = await Promise.all([
        supabase.from('calls').select('*').eq('agent_id', user.id).gte('created_at', startDate),
        supabase.from('leads').select('*').eq('assigned_to', user.id).gte('created_at', startDate)
      ]);

      const calls = callsRes.data || [];
      const leads = leadsRes.data || [];
      const convertedLeads = leads.filter(l => l.status === 'converti');
      
      const totalCalls = calls.length;
      const answered = calls.filter(c => c.status === 'answered').length;
      const missed = calls.filter(c => c.status === 'missed').length;
      const totalDuration = calls.reduce((acc, c) => acc + (c.duration || 0), 0);
      const qualifiedLeads = leads.filter(l => l.status === 'qualifié').length;
      const convertedLeadsCount = convertedLeads.length;
      const commission = convertedLeadsCount * 50;

      // Données pour graphique historique
      const history = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        history.push({
          date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
          appels: Math.floor(Math.random() * 20 + 5),
          leads: Math.floor(Math.random() * 10 + 2),
          conversions: Math.floor(Math.random() * 5)
        });
      }
      setChartData(history);

      if (isMounted.current) {
        setStats({
          calls: {
            total: totalCalls,
            answered,
            missed,
            answered_rate: totalCalls ? Math.round((answered / totalCalls) * 100) : 0,
            total_duration: totalDuration,
            avg_duration: totalCalls ? Math.round(totalDuration / totalCalls) : 0
          },
          leads: {
            total: leads.length,
            qualified: qualifiedLeads,
            converted: convertedLeadsCount,
            conversion_rate: leads.length ? Math.round((convertedLeadsCount / leads.length) * 100) : 0,
            commission
          }
        });

        const targets = { 
          calls: totalCalls, 
          leads: leads.length, 
          qualified: qualifiedLeads, 
          converted: convertedLeadsCount,
          commission 
        };
        
        const interval = setInterval(() => {
          setAnimatedStats(prev => ({
            calls: Math.min((prev.calls || 0) + Math.ceil(targets.calls / 20), targets.calls),
            leads: Math.min((prev.leads || 0) + Math.ceil(targets.leads / 20), targets.leads),
            qualified: Math.min((prev.qualified || 0) + Math.ceil(targets.qualified / 20), targets.qualified),
            converted: Math.min((prev.converted || 0) + Math.ceil(targets.converted / 20), targets.converted),
            commission: Math.min((prev.commission || 0) + Math.ceil(targets.commission / 20), targets.commission)
          }));
        }, 50);

        setTimeout(() => {
          clearInterval(interval);
          setAnimatedStats(targets);
        }, 1000);
      }
    } catch (error) {
      console.error('Erreur stats agent:', error);
    }
  };

  // ========== HANDLERS AGENT ==========
  const handleStartDay = async () => {
  try {
    const { data, error } = await supabase
      .from('agent_presences')
      .upsert({
        agent_id: user.id,
        date: today,
        start_time: new Date().toISOString(),
        status: 'present',
        end_time: null,
        pause_start: null,
        pause_end: null,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'agent_id, date',
        ignoreDuplicates: false
      })
      .select()
      .single();
      
    if (error) throw error;
    setPresence(data);
    toast.success('Journée commencée !');
  } catch (error) {
    console.error('Erreur:', error);
    toast.error('Erreur: ' + error.message);
  }
};

  const handleEndDay = async () => {
    try {
      const endTime = new Date();
      const totalMinutes = Math.round((endTime - new Date(presence.start_time)) / (1000 * 60));
      
      const totalPause1 = (presence.total_pause1_minutes || 0) + Math.round(pause1Seconds / 60);
      const totalPause2 = (presence.total_pause2_minutes || 0) + Math.round(pause2Seconds / 60);
      const totalLunch = (presence.total_lunch_minutes || 0) + Math.round(lunchSeconds / 60);
      
      const { error } = await supabase
        .from('agent_presences')
        .update({
          end_time: endTime,
          status: 'ended',
          total_working_minutes: totalMinutes,
          total_pause1_minutes: totalPause1,
          total_pause2_minutes: totalPause2,
          total_lunch_minutes: totalLunch
        })
        .eq('id', presence.id);
      if (error) throw error;
      toast.success('Journée terminée !');
      setPresence(null);
      setWorkingSeconds(0); setPause1Seconds(0); setPause2Seconds(0); setLunchSeconds(0);
    } catch (error) {
      toast.error('Erreur: ' + error.message);
    }
  };

  const handleStartPause = async (type) => {
    try {
      const now = new Date();
      const { error } = await supabase
        .from('agent_presences')
        .update({ pause_start: now, pause_type: type, status: 'pause' })
        .eq('id', presence.id);
      if (error) throw error;
      setIsPaused(true);
      setPauseType(type);
      setPauseStart(now);
      const names = { pause1: 'Pause 1', pause2: 'Pause 2', lunch: 'Repas' };
      toast.info(`${names[type]} commencée`);
    } catch (error) {
      toast.error('Erreur: ' + error.message);
    }
  };

  const handleEndPause = async () => {
    try {
      const now = new Date();
      let minutes = 0;
      let totalField = '';
      
      if (pauseType === 'pause1') {
        minutes = Math.round(pause1Seconds / 60);
        totalField = 'total_pause1_minutes';
      } else if (pauseType === 'pause2') {
        minutes = Math.round(pause2Seconds / 60);
        totalField = 'total_pause2_minutes';
      } else if (pauseType === 'lunch') {
        minutes = Math.round(lunchSeconds / 60);
        totalField = 'total_lunch_minutes';
      }

      const updates = { 
        pause_end: now, 
        status: 'present',
        [totalField]: (presence[totalField] || 0) + minutes
      };

      const { error } = await supabase
        .from('agent_presences')
        .update(updates)
        .eq('id', presence.id);
        
      if (error) throw error;
      
      await loadAgentPresence();
      
      setIsPaused(false);
      setPauseType(null);
      setPauseStart(null);
      setPause1Seconds(0);
      setPause2Seconds(0);
      setLunchSeconds(0);
      
      toast.success('Pause terminée');
    } catch (error) {
      toast.error('Erreur: ' + error.message);
    }
  };

  // ========== REALTIME AGENT ==========
  useEffect(() => {
    if (!isAgent || !user) return;

    const channel = supabase
      .channel('agent-stats-live')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calls'
        },
        (payload) => {
          if (payload.new?.agent_id === user.id || payload.old?.agent_id === user.id) {
            loadAgentStats();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leads'
        },
        (payload) => {
          if (payload.new?.assigned_to === user.id || payload.old?.assigned_to === user.id) {
            loadAgentStats();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAgent, user]);

  // ========== MANAGER FUNCTIONS ==========
  const loadTeamMembers = async () => {
    if (!isManager) return;
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'agent');
    if (!error) setTeamMembers(data || []);
  };

  const loadTeamPresence = async () => {
    if (!isManager || !teamMembers.length) return;
    const ids = teamMembers.map(m => m.id);
    const { data, error } = await supabase
      .from('agent_presences')
      .select('*')
      .in('agent_id', ids)
      .eq('date', today);
    if (!error && data) {
      const map = {};
      data.forEach(p => { map[p.agent_id] = p; });
      setTeamPresence(map);
      setLastUpdate(new Date());
    }
  };

  const loadTeamStats = async () => {
    if (!isManager || !teamMembers.length) return;
    const startDate = getStartDate();
    const ids = teamMembers.map(m => m.id);

    const [callsRes, leadsRes] = await Promise.all([
      supabase.from('calls').select('*').in('agent_id', ids).gte('created_at', startDate),
      supabase.from('leads').select('*').in('assigned_to', ids).gte('created_at', startDate)
    ]);

    const calls = callsRes.data || [];
    const leads = leadsRes.data || [];

    const stats = {};
    const comms = {};
    
    ids.forEach(id => {
      const agentCalls = calls.filter(c => c.agent_id === id);
      const agentLeads = leads.filter(l => l.assigned_to === id);
      const convertedLeads = agentLeads.filter(l => l.status === 'converti');
      
      stats[id] = {
        calls: agentCalls.length,
        answered: agentCalls.filter(c => c.status === 'answered').length,
        missed: agentCalls.filter(c => c.status === 'missed').length,
        duration: agentCalls.reduce((acc, c) => acc + (c.duration || 0), 0),
        qualified: agentLeads.filter(l => l.status === 'qualifié').length,
        converted: convertedLeads.length,
        commission: convertedLeads.length * 50
      };
      
      comms[id] = convertedLeads.length * 50;
    });

    setTeamStats(stats);
    setCommissions(comms);

    // Leaderboard
    const leaderData = ids.map(id => ({
      id,
      name: teamMembers.find(m => m.id === id)?.full_name || 'Agent',
      conversions: stats[id].converted || 0,
      calls: stats[id].calls || 0,
      rate: stats[id].qualified ? Math.round((stats[id].converted / stats[id].qualified) * 100) : 0,
      commission: comms[id] || 0
    })).sort((a, b) => b.commission - a.commission || b.conversions - a.conversions);
    
    setLeaderboard(leaderData);

    // Chart data
    const chart = [];
    const days = selectedPeriod === 'daily' ? 1 : selectedPeriod === 'weekly' ? 7 : 30;
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      chart.push({
        date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        appels: Math.floor(Math.random() * 50),
        leads: Math.floor(Math.random() * 30),
        conversions: Math.floor(Math.random() * 10)
      });
    }
    setChartData(chart.reverse());

    const totalCalls = Object.values(stats).reduce((acc, s) => acc + s.calls, 0);
    const totalQualified = Object.values(stats).reduce((acc, s) => acc + s.qualified, 0);
    const totalConverted = Object.values(stats).reduce((acc, s) => acc + s.converted, 0);
    const totalCommission = Object.values(comms).reduce((acc, c) => acc + c, 0);
    
    const targets = { totalCalls, totalQualified, totalConverted, totalCommission };
    const interval = setInterval(() => {
      setAnimatedStats(prev => ({
        ...prev,
        totalCalls: Math.min((prev.totalCalls || 0) + Math.ceil(targets.totalCalls / 20), targets.totalCalls),
        totalQualified: Math.min((prev.totalQualified || 0) + Math.ceil(targets.totalQualified / 20), targets.totalQualified),
        totalConverted: Math.min((prev.totalConverted || 0) + Math.ceil(targets.totalConverted / 20), targets.totalConverted),
        totalCommission: Math.min((prev.totalCommission || 0) + Math.ceil(targets.totalCommission / 20), targets.totalCommission)
      }));
    }, 50);

    setTimeout(() => {
      clearInterval(interval);
      setAnimatedStats(targets);
    }, 1000);
  };

  // ========== REALTIME MANAGER ==========
  useEffect(() => {
    if (!isManager || !teamMembers.length) return;

    const teamIds = teamMembers.map(m => m.id);

    const channel = supabase
      .channel('manager-live')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'agent_presences'
        },
        (payload) => {
          const row = payload.new || payload.old;
          if (!teamIds.includes(row.agent_id)) return;
          if (row.date !== today) return;

          setTeamPresence(prev => ({
            ...prev,
            [row.agent_id]: row
          }));
          setLastUpdate(new Date());
          setConnectionStatus('connected');
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calls'
        },
        () => {
          loadTeamStats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leads'
        },
        () => {
          loadTeamStats();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setConnectionStatus('connected');
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isManager, teamMembers]);

  // ========== EXPORT MANAGER ==========
  const exportToExcel = () => {
    const data = teamMembers.map(agent => {
      const presence = teamPresence[agent.id];
      const stats = teamStats[agent.id] || {};
      return {
        Agent: agent.full_name,
        Email: agent.email,
        Statut: getStatusBadge(presence).label,
        'Début journée': presence?.start_time ? formatTime(new Date(presence.start_time)) : '-',
        Appels: stats.calls || 0,
        'Appels réussis': stats.answered || 0,
        'Leads qualifiés': stats.qualified || 0,
        Conversions: stats.converted || 0,
        'Taux conversion': stats.qualified ? Math.round((stats.converted / stats.qualified) * 100) : 0,
        'Commission (€)': commissions[agent.id] || 0,
        'Pause 1 (min)': presence?.total_pause1_minutes || 0,
        'Pause 2 (min)': presence?.total_pause2_minutes || 0,
        'Repas (min)': presence?.total_lunch_minutes || 0
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Performance');
    XLSX.writeFile(wb, `performance_equipe_${today}.xlsx`);
  };

  // ========== STATUS BADGE ==========
  const getStatusBadge = (presence) => {
    if (!presence) {
      return {
        label: 'Absent',
        icon: CircleOff,
        color: 'bg-gray-500',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-700',
        borderColor: 'border-gray-300',
        pulse: false
      };
    }

    if (presence.pause_start && !presence.pause_end) {
      const type = presence.pause_type;
      if (type === 'pause1') {
        return {
          label: 'Pause 1',
          icon: Coffee,
          color: 'bg-yellow-500',
          bgColor: 'bg-yellow-50',
          textColor: 'text-yellow-700',
          borderColor: 'border-yellow-400',
          pulse: true
        };
      }
      if (type === 'pause2') {
        return {
          label: 'Pause 2',
          icon: Coffee,
          color: 'bg-orange-500',
          bgColor: 'bg-orange-50',
          textColor: 'text-orange-700',
          borderColor: 'border-orange-400',
          pulse: true
        };
      }
      if (type === 'lunch') {
        return {
          label: 'Repas',
          icon: Utensils,
          color: 'bg-red-500',
          bgColor: 'bg-red-50',
          textColor: 'text-red-700',
          borderColor: 'border-red-400',
          pulse: true
        };
      }
    }

    if (presence.status === 'present' && !presence.pause_start) {
      return {
        label: 'En ligne',
        icon: Activity,
        color: 'bg-green-500',
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        borderColor: 'border-green-400',
        pulse: true
      };
    }

    if (presence.status === 'ended' || presence.end_time) {
      return {
        label: 'Terminé',
        icon: LogOut,
        color: 'bg-gray-500',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-700',
        borderColor: 'border-gray-300',
        pulse: false
      };
    }

    return {
      label: 'Absent',
      icon: CircleOff,
      color: 'bg-gray-500',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-300',
      pulse: false
    };
  };

  // ========== CALCUL TEMPS ACTIF ==========
  const calculateActiveTime = (presence) => {
    if (!presence?.start_time) return 0;
    if (presence.end_time) {
      const end = new Date(presence.end_time);
      const start = new Date(presence.start_time);
      const totalMinutes = Math.floor((end - start) / 60000);
      const pauseTotal = (presence.total_pause1_minutes || 0) + 
                        (presence.total_pause2_minutes || 0) + 
                        (presence.total_lunch_minutes || 0);
      return totalMinutes - pauseTotal;
    }
    const now = Date.now();
    const start = new Date(presence.start_time).getTime();
    const totalMinutes = Math.floor((now - start) / 60000);
    let currentPause = 0;
    if (presence.pause_start && !presence.pause_end) {
      const pauseStart = new Date(presence.pause_start).getTime();
      currentPause = Math.floor((now - pauseStart) / 60000);
    }
    const pauseTotal = (presence.total_pause1_minutes || 0) + 
                      (presence.total_pause2_minutes || 0) + 
                      (presence.total_lunch_minutes || 0) + 
                      currentPause;
    return totalMinutes - pauseTotal;
  };

  // ========== CHARGEMENT INITIAL ==========
  useEffect(() => {
    isMounted.current = true;
    
    const loadData = async () => {
      setLoading(true);
      if (isAgent && user) {
        await loadAgentPresence();
        await loadAgentStats();
      } else if (isManager) {
        await loadTeamMembers();
      }
      setLoading(false);
    };

    loadData();

    return () => { isMounted.current = false; };
  }, [isAgent, isManager, user]);

  // Chargement team stats après avoir les membres
  useEffect(() => {
    if (isManager && teamMembers.length > 0) {
      loadTeamPresence();
      loadTeamStats();
    }
  }, [isManager, teamMembers, selectedPeriod]);

  // Stats pour manager
  const onlineCount = isManager ? Object.values(teamPresence).filter(p => p?.status === 'present' && !p?.pause_start).length : 0;
  const pauseCount = isManager ? Object.values(teamPresence).filter(p => p?.pause_start && !p?.pause_end).length : 0;
  const filteredMembers = isManager && selectedAgent === 'all' 
    ? teamMembers 
    : teamMembers.filter(m => m.id === selectedAgent);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDark ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="relative">
          <div className="animate-spin w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-blue-600 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  // ========== RENDER AGENT ==========
  if (isAgent) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white' 
          : 'bg-gradient-to-br from-gray-50 via-white to-gray-50 text-gray-900'
      } p-8`}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className={`${
            isDark ? 'bg-white/10 border-white/20' : 'bg-white/80 border-gray-200'
          } backdrop-blur-xl rounded-3xl shadow-2xl p-8 mb-8 border`}>
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className={`text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent`}>
                    Mon tableau de bord
                  </h1>
                  <Crown className="w-8 h-8 text-yellow-400" />
                </div>
                <div className="flex items-center gap-4 text-gray-400">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date().toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    {presence?.status === 'present' ? (
                      <>
                        <Wifi className="w-4 h-4 text-green-400" />
                        <span className="text-green-400">En ligne</span>
                      </>
                    ) : (
                      <>
                        <WifiOff className="w-4 h-4 text-red-400" />
                        <span className="text-red-400">Hors ligne</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={toggleTheme}
                  className={`p-3 rounded-xl ${
                    isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'
                  } transition-all`}
                >
                  {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                
                <select 
                  value={selectedPeriod} 
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className={`px-6 py-3 rounded-xl border ${
                    isDark 
                      ? 'bg-white/10 border-white/20 text-white' 
                      : 'bg-white border-gray-200 text-gray-900'
                  } backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="daily" className={isDark ? 'bg-gray-800' : 'bg-white'}>Aujourd'hui</option>
                  <option value="weekly" className={isDark ? 'bg-gray-800' : 'bg-white'}>Cette semaine</option>
                  <option value="monthly" className={isDark ? 'bg-gray-800' : 'bg-white'}>Ce mois</option>
                </select>
              </div>
            </div>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className={`${
              isDark ? 'bg-blue-600/20 border-blue-500/30' : 'bg-blue-50 border-blue-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`${isDark ? 'text-blue-200' : 'text-blue-600'} text-sm mb-1`}>Statut</p>
                  <p className="text-2xl font-bold">
                    {!presence ? 'Hors ligne' : isPaused ? 'En pause' : 'En ligne'}
                  </p>
                </div>
                <div className={`p-3 ${isDark ? 'bg-blue-500/30' : 'bg-blue-100'} rounded-xl`}>
                  {!presence ? <Power className="w-6 h-6 text-gray-400" /> : 
                   isPaused ? <Pause className="w-6 h-6 text-yellow-500" /> : 
                   <Activity className="w-6 h-6 text-green-500" />}
                </div>
              </div>
              <p className="mt-2 text-3xl font-mono">{formatTime(currentTime)}</p>
            </div>

            <div className={`${
              isDark ? 'bg-purple-600/20 border-purple-500/30' : 'bg-purple-50 border-purple-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`${isDark ? 'text-purple-200' : 'text-purple-600'} text-sm mb-1`}>Temps travaillé</p>
                  <p className="text-2xl font-bold">{formatDuration(workingSeconds)}</p>
                </div>
                <div className={`p-3 ${isDark ? 'bg-purple-500/30' : 'bg-purple-100'} rounded-xl`}>
                  <Timer className={`w-6 h-6 ${isDark ? 'text-purple-300' : 'text-purple-600'}`} />
                </div>
              </div>
            </div>

            <div className={`${
              isDark ? 'bg-green-600/20 border-green-500/30' : 'bg-green-50 border-green-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`${isDark ? 'text-green-200' : 'text-green-600'} text-sm mb-1`}>Commission</p>
                  <p className="text-2xl font-bold">{formatCurrency(animatedStats.commission || 0)}</p>
                </div>
                <div className={`p-3 ${isDark ? 'bg-green-500/30' : 'bg-green-100'} rounded-xl`}>
                  <DollarSign className={`w-6 h-6 ${isDark ? 'text-green-300' : 'text-green-600'}`} />
                </div>
              </div>
            </div>

            <div className={`${
              isDark ? 'bg-orange-600/20 border-orange-500/30' : 'bg-orange-50 border-orange-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`${isDark ? 'text-orange-200' : 'text-orange-600'} text-sm mb-1`}>Taux conversion</p>
                  <p className="text-2xl font-bold">{stats?.leads?.conversion_rate || 0}%</p>
                </div>
                <div className={`p-3 ${isDark ? 'bg-orange-500/30' : 'bg-orange-100'} rounded-xl`}>
                  <TrendingUp className={`w-6 h-6 ${isDark ? 'text-orange-300' : 'text-orange-600'}`} />
                </div>
              </div>
            </div>
          </div>

          {/* Boutons de contrôle statut */}
          {!presence ? (
            <button
              onClick={handleStartDay}
              className="w-full mb-8 bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 rounded-2xl text-xl font-bold hover:scale-105 transition-all shadow-lg flex items-center justify-center gap-3"
            >
              <Play className="w-6 h-6" />
              Démarrer la journée
            </button>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <button
                onClick={handleEndDay}
                className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <LogOut className="w-5 h-5" />
                Terminer
              </button>
              
              {!isPaused ? (
                <>
                  <button
                    onClick={() => handleStartPause('pause1')}
                    className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white p-4 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    <Coffee className="w-5 h-5" />
                    Pause 1
                  </button>
                  <button
                    onClick={() => handleStartPause('pause2')}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    <Coffee className="w-5 h-5" />
                    Pause 2
                  </button>
                  <button
                    onClick={() => handleStartPause('lunch')}
                    className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    <Utensils className="w-5 h-5" />
                    Repas
                  </button>
                </>
              ) : (
                <button
                  onClick={handleEndPause}
                  className="col-span-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg flex items-center justify-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  Reprendre le travail
                </button>
              )}
            </div>
          )}

          {/* Stats principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className={`${
              isDark ? 'bg-white/10 border-white/20' : 'bg-white border-gray-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex items-center gap-3 mb-2">
                <Phone className="w-5 h-5 text-blue-500" />
                <h3 className="font-semibold">Appels</h3>
              </div>
              <p className="text-3xl font-bold">{animatedStats.calls}</p>
              <p className="text-sm text-gray-500 mt-1">{stats?.calls?.answered || 0} réussis · {stats?.calls?.missed || 0} manqués</p>
            </div>

            <div className={`${
              isDark ? 'bg-white/10 border-white/20' : 'bg-white border-gray-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex items-center gap-3 mb-2">
                <Target className="w-5 h-5 text-green-500" />
                <h3 className="font-semibold">Leads</h3>
              </div>
              <p className="text-3xl font-bold">{animatedStats.leads}</p>
              <p className="text-sm text-gray-500 mt-1">{stats?.leads?.qualified || 0} qualifiés</p>
            </div>

            <div className={`${
              isDark ? 'bg-white/10 border-white/20' : 'bg-white border-gray-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-purple-500" />
                <h3 className="font-semibold">Conversions</h3>
              </div>
              <p className="text-3xl font-bold">{animatedStats.converted}</p>
              <p className="text-sm text-gray-500 mt-1">{formatCurrency(animatedStats.commission)}</p>
            </div>

            <div className={`${
              isDark ? 'bg-white/10 border-white/20' : 'bg-white border-gray-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-orange-500" />
                <h3 className="font-semibold">Durée moyenne</h3>
              </div>
              <p className="text-3xl font-bold">{Math.floor((stats?.calls?.avg_duration || 0) / 60)}min</p>
              <p className="text-sm text-gray-500 mt-1">par appel</p>
            </div>
          </div>

          {/* Graphique historique */}
          <div className={`${
            isDark ? 'bg-white/10 border-white/20' : 'bg-white border-gray-200'
          } backdrop-blur-xl rounded-3xl shadow-xl p-6 border`}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <LineChart className="w-5 h-5 text-blue-400" />
              Historique des performances
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <ReLineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                <XAxis dataKey="date" stroke={isDark ? '#9ca3af' : '#6b7280'} />
                <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDark ? '#1f2937' : '#ffffff',
                    borderColor: isDark ? '#374151' : '#e5e7eb',
                    color: isDark ? '#ffffff' : '#000000'
                  }} 
                />
                <Legend />
                <Line type="monotone" dataKey="appels" stroke="#3b82f6" strokeWidth={2} />
                <Line type="monotone" dataKey="leads" stroke="#10b981" strokeWidth={2} />
                <Line type="monotone" dataKey="conversions" stroke="#f59e0b" strokeWidth={2} />
              </ReLineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  }

  // ========== RENDER MANAGER ==========
  if (isManager) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white' 
          : 'bg-gradient-to-br from-gray-50 via-white to-gray-50 text-gray-900'
      } p-8`}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className={`${
            isDark ? 'bg-white/10 border-white/20' : 'bg-white/80 border-gray-200'
          } backdrop-blur-xl rounded-3xl shadow-2xl p-8 mb-8 border`}>
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className={`text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent`}>
                    Tableau de bord Manager
                  </h1>
                  <Crown className="w-8 h-8 text-yellow-400" />
                </div>
                <div className="flex items-center gap-4 text-gray-400">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date().toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    {connectionStatus === 'connected' ? (
                      <>
                        <Wifi className="w-4 h-4 text-green-400" />
                        <span className="text-green-400">Temps réel</span>
                      </>
                    ) : (
                      <>
                        <WifiOff className="w-4 h-4 text-red-400" />
                        <span className="text-red-400">Déconnecté</span>
                      </>
                    )}
                  </div>
                  {lastUpdate && (
                    <div className="flex items-center gap-2 text-sm">
                      <RefreshCw className="w-3 h-3" />
                      {lastUpdate.toLocaleTimeString()}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={toggleTheme}
                  className={`p-3 rounded-xl ${
                    isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'
                  } transition-all`}
                >
                  {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                
                <button
                  onClick={exportToExcel}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl ${
                    isDark 
                      ? 'bg-white/10 hover:bg-white/20' 
                      : 'bg-gray-100 hover:bg-gray-200'
                  } transition-all`}
                >
                  <DownloadIcon className="w-4 h-4" />
                  Export
                </button>

                <select 
                  value={selectedPeriod} 
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className={`px-6 py-3 rounded-xl border ${
                    isDark 
                      ? 'bg-white/10 border-white/20 text-white' 
                      : 'bg-white border-gray-200 text-gray-900'
                  } backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="daily" className={isDark ? 'bg-gray-800' : 'bg-white'}>Aujourd'hui</option>
                  <option value="weekly" className={isDark ? 'bg-gray-800' : 'bg-white'}>Cette semaine</option>
                  <option value="monthly" className={isDark ? 'bg-gray-800' : 'bg-white'}>Ce mois</option>
                </select>

                <select 
                  value={selectedAgent} 
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className={`px-6 py-3 rounded-xl border ${
                    isDark 
                      ? 'bg-white/10 border-white/20 text-white' 
                      : 'bg-white border-gray-200 text-gray-900'
                  } backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="all" className={isDark ? 'bg-gray-800' : 'bg-white'}>Toute l'équipe</option>
                  {teamMembers.map(agent => (
                    <option key={agent.id} value={agent.id} className={isDark ? 'bg-gray-800' : 'bg-white'}>
                      {agent.full_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className={`${
              isDark ? 'bg-blue-600/20 border-blue-500/30' : 'bg-blue-50 border-blue-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`${isDark ? 'text-blue-200' : 'text-blue-600'} text-sm mb-1`}>Équipe</p>
                  <p className="text-3xl font-bold">{teamMembers.length}</p>
                </div>
                <div className={`p-3 ${isDark ? 'bg-blue-500/30' : 'bg-blue-100'} rounded-xl`}>
                  <Users className={`w-6 h-6 ${isDark ? 'text-blue-300' : 'text-blue-600'}`} />
                </div>
              </div>
              <div className="mt-4 flex gap-2 text-xs">
                <span className="px-2 py-1 bg-green-500/20 text-green-600 rounded-full">{onlineCount} en ligne</span>
                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-600 rounded-full">{pauseCount} en pause</span>
              </div>
            </div>

            <div className={`${
              isDark ? 'bg-purple-600/20 border-purple-500/30' : 'bg-purple-50 border-purple-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`${isDark ? 'text-purple-200' : 'text-purple-600'} text-sm mb-1`}>Appels</p>
                  <p className="text-3xl font-bold">{animatedStats.totalCalls || 0}</p>
                </div>
                <div className={`p-3 ${isDark ? 'bg-purple-500/30' : 'bg-purple-100'} rounded-xl`}>
                  <Phone className={`w-6 h-6 ${isDark ? 'text-purple-300' : 'text-purple-600'}`} />
                </div>
              </div>
            </div>

            <div className={`${
              isDark ? 'bg-green-600/20 border-green-500/30' : 'bg-green-50 border-green-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`${isDark ? 'text-green-200' : 'text-green-600'} text-sm mb-1`}>Leads qual.</p>
                  <p className="text-3xl font-bold">{animatedStats.totalQualified || 0}</p>
                </div>
                <div className={`p-3 ${isDark ? 'bg-green-500/30' : 'bg-green-100'} rounded-xl`}>
                  <Target className={`w-6 h-6 ${isDark ? 'text-green-300' : 'text-green-600'}`} />
                </div>
              </div>
            </div>

            <div className={`${
              isDark ? 'bg-orange-600/20 border-orange-500/30' : 'bg-orange-50 border-orange-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`${isDark ? 'text-orange-200' : 'text-orange-600'} text-sm mb-1`}>Conversions</p>
                  <p className="text-3xl font-bold">{animatedStats.totalConverted || 0}</p>
                </div>
                <div className={`p-3 ${isDark ? 'bg-orange-500/30' : 'bg-orange-100'} rounded-xl`}>
                  <TrendingUp className={`w-6 h-6 ${isDark ? 'text-orange-300' : 'text-orange-600'}`} />
                </div>
              </div>
            </div>

            <div className={`${
              isDark ? 'bg-pink-600/20 border-pink-500/30' : 'bg-pink-50 border-pink-200'
            } rounded-2xl shadow-xl p-6 border`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`${isDark ? 'text-pink-200' : 'text-pink-600'} text-sm mb-1`}>Commissions</p>
                  <p className="text-2xl font-bold">{formatCurrency(animatedStats.totalCommission || 0)}</p>
                </div>
                <div className={`p-3 ${isDark ? 'bg-pink-500/30' : 'bg-pink-100'} rounded-xl`}>
                  <DollarSign className={`w-6 h-6 ${isDark ? 'text-pink-300' : 'text-pink-600'}`} />
                </div>
              </div>
            </div>
          </div>

          {/* Graphiques */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className={`${
              isDark ? 'bg-white/10 border-white/20' : 'bg-white border-gray-200'
            } backdrop-blur-xl rounded-3xl shadow-xl p-6 border`}>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <LineChart className="w-5 h-5 text-blue-400" />
                Évolution des performances
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <ReLineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                  <XAxis dataKey="date" stroke={isDark ? '#9ca3af' : '#6b7280'} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDark ? '#1f2937' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#ffffff' : '#000000'
                    }} 
                  />
                  <Legend />
                  <Line type="monotone" dataKey="appels" stroke="#3b82f6" strokeWidth={2} />
                  <Line type="monotone" dataKey="leads" stroke="#10b981" strokeWidth={2} />
                  <Line type="monotone" dataKey="conversions" stroke="#f59e0b" strokeWidth={2} />
                </ReLineChart>
              </ResponsiveContainer>
            </div>

            <div className={`${
              isDark ? 'bg-white/10 border-white/20' : 'bg-white border-gray-200'
            } backdrop-blur-xl rounded-3xl shadow-xl p-6 border`}>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-purple-400" />
                Répartition des appels
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <RePieChart>
                  <Pie
                    data={[
                      { name: 'Réussis', value: Object.values(teamStats).reduce((acc, s) => acc + (s.answered || 0), 0) },
                      { name: 'Manqués', value: Object.values(teamStats).reduce((acc, s) => acc + (s.missed || 0), 0) }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    <Cell key="success" fill="#10b981" />
                    <Cell key="missed" fill="#ef4444" />
                  </Pie>
                  <Tooltip />
                </RePieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Agents en direct */}
          <div className={`${
            isDark ? 'bg-white/10 border-white/20' : 'bg-white border-gray-200'
          } backdrop-blur-xl rounded-3xl shadow-2xl p-8 mb-8 border`}>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Activity className="w-6 h-6 text-blue-400" />
              Agents en direct
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredMembers.map(agent => {
                const presence = teamPresence[agent.id];
                const stats = teamStats[agent.id] || {};
                const status = getStatusBadge(presence);
                const Icon = status.icon;
                const activeTime = calculateActiveTime(presence);

                let currentPause = 0;
                let currentPauseSeconds = 0;
                if (presence?.pause_start && !presence?.pause_end) {
                  const now = Date.now();
                  const start = new Date(presence.pause_start).getTime();
                  currentPause = Math.floor((now - start) / 60000);
                  currentPauseSeconds = Math.floor((now - start) / 1000) % 60;
                }

                return (
                  <div 
                    key={agent.id}
                    className={`${status.bgColor} rounded-xl p-5 border-2 ${status.borderColor} shadow-lg hover:shadow-xl transition-all transform hover:scale-102 relative overflow-hidden group`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>

                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                          {agent.full_name?.[0] || agent.email?.[0] || 'A'}
                        </div>
                        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white ${status.color} flex items-center justify-center`}>
                          <Icon className="w-3 h-3 text-white" />
                        </div>
                        {status.pulse && (
                          <span className="absolute -top-1 -right-1 flex h-3 w-3">
                            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${status.color} opacity-75`}></span>
                          </span>
                        )}
                      </div>

                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 text-lg mb-1">{agent.full_name || agent.email}</h3>
                        
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${status.color} text-white font-medium`}>
                            {status.label}
                          </span>
                          {presence?.pause_start && !presence?.pause_end && (
                            <span className="text-xs text-gray-600 bg-white/60 px-2 py-1 rounded-full">
                              {currentPause}min {currentPauseSeconds}s
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-3 gap-1 mt-3">
                          <div className="bg-blue-100 rounded-lg p-2 text-center">
                            <p className="text-xs text-blue-600 font-bold">{stats.calls || 0}</p>
                            <p className="text-[10px] text-gray-600">Appels</p>
                          </div>
                          <div className="bg-green-100 rounded-lg p-2 text-center">
                            <p className="text-xs text-green-600 font-bold">{stats.qualified || 0}</p>
                            <p className="text-[10px] text-gray-600">Qual.</p>
                          </div>
                          <div className="bg-purple-100 rounded-lg p-2 text-center">
                            <p className="text-xs text-purple-600 font-bold">
                              {stats.qualified ? Math.round((stats.converted / stats.qualified) * 100) : 0}%
                            </p>
                            <p className="text-[10px] text-gray-600">Taux</p>
                          </div>
                        </div>

                        <div className="mt-2 flex items-center justify-between text-xs">
                          <span className="text-gray-600">
                            ⏱️ Actif: {formatMinutes(activeTime)}
                          </span>
                          <span className="text-gray-600">
                            💰 {formatCurrency(commissions[agent.id] || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Leaderboard */}
          <div className={`${
            isDark ? 'bg-white/10 border-white/20' : 'bg-white border-gray-200'
          } backdrop-blur-xl rounded-3xl shadow-2xl p-8 mb-8 border`}>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-400" />
              Top Performers
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {leaderboard.slice(0, 3).map((agent, index) => (
                <div key={agent.id} className={`${
                  index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                  index === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-600' :
                  'bg-gradient-to-br from-orange-400 to-orange-600'
                } rounded-xl p-6 text-white`}>
                  <div className="flex items-center gap-4">
                    <div className="text-4xl font-bold">#{index + 1}</div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{agent.name}</h3>
                      <p className="text-sm opacity-90">{agent.conversions} conversions</p>
                      <p className="text-sm opacity-90">{agent.rate}% taux</p>
                      <p className="text-lg font-bold mt-2">{formatCurrency(agent.commission)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tableau complet */}
          <div className={`${
            isDark ? 'bg-white/10 border-white/20' : 'bg-white border-gray-200'
          } backdrop-blur-xl rounded-3xl shadow-2xl p-8 border`}>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-400" />
              Performance détaillée
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="px-6 py-4 text-left">Agent</th>
                    <th className="px-6 py-4 text-left">Statut</th>
                    <th className="px-6 py-4 text-left">Temps actif</th>
                    <th className="px-6 py-4 text-left">Appels</th>
                    <th className="px-6 py-4 text-left">Qualifiés</th>
                    <th className="px-6 py-4 text-left">Conversions</th>
                    <th className="px-6 py-4 text-left">Taux</th>
                    <th className="px-6 py-4 text-left">Commission</th>
                    <th className="px-6 py-4 text-left">Pauses</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map(agent => {
                    const presence = teamPresence[agent.id];
                    const stats = teamStats[agent.id] || {};
                    const status = getStatusBadge(presence);
                    const Icon = status.icon;
                    const activeTime = calculateActiveTime(presence);
                    const totalPauses = (presence?.total_pause1_minutes || 0) + 
                                       (presence?.total_pause2_minutes || 0) + 
                                       (presence?.total_lunch_minutes || 0);

                    return (
                      <tr key={agent.id} className="border-b border-white/10 hover:bg-white/5">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                              {agent.full_name?.[0] || 'A'}
                            </div>
                            <span>{agent.full_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${status.textColor}`} />
                            <span className={status.textColor}>{status.label}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">{formatMinutes(activeTime)}</td>
                        <td className="px-6 py-4 font-semibold">{stats.calls || 0}</td>
                        <td className="px-6 py-4">{stats.qualified || 0}</td>
                        <td className="px-6 py-4">{stats.converted || 0}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            stats.qualified ? 
                              (stats.converted / stats.qualified) * 100 >= 50 ? 'bg-green-500/30 text-green-300' :
                              (stats.converted / stats.qualified) * 100 >= 30 ? 'bg-yellow-500/30 text-yellow-300' :
                              'bg-red-500/30 text-red-300'
                            : 'bg-gray-500/30 text-gray-300'
                          }`}>
                            {stats.qualified ? Math.round((stats.converted / stats.qualified) * 100) : 0}%
                          </span>
                        </td>
                        <td className="px-6 py-4">{formatCurrency(commissions[agent.id] || 0)}</td>
                        <td className="px-6 py-4">{formatMinutes(totalPauses)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}